import { html, LitElement, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { styleMap } from "lit/directives/style-map.js";

import css from "./bible-navigation-menu.css?inline";

@customElement("bible-navigation-menu")
export class NavigationMenuElement extends LitElement {
  static styles = unsafeCSS(css);

  private disconnectedCallbacks: (() => void)[] = [];

  private get menuProperties() {
    if (!this.observerEntry || !this.trigger) return {};
    const height = this.observerEntry.borderBoxSize[0]?.blockSize ?? 0;
    const width = this.observerEntry.borderBoxSize[0]?.inlineSize ?? 0;
    const triggerOffsetLeft = this.refs.content.value!.offsetLeft ?? 0;
    const { offsetLeft: triggerLeft, offsetWidth: triggerWidth } = this.trigger;
    const navigationRect = document
      .querySelector("bible-navigation")!
      .getBoundingClientRect();
    return getMenuProperties({
      width,
      height,
      offsetTop: navigationRect.top + navigationRect.height,
      triggerLeft: triggerLeft - triggerOffsetLeft,
      triggerWidth,
    });
  }

  @state()
  private observerEntry: ResizeObserverEntry | null = null;

  private refs = {
    content: createRef<HTMLElement>(),
    resize: createRef<HTMLElement>(),
  };

  @property()
  trigger: HTMLElement | null = null;

  disconnectedCallback() {
    super.disconnectedCallback();
    while (this.disconnectedCallbacks.length > 0)
      this.disconnectedCallbacks.shift()!();
  }

  firstUpdated() {
    this.withCleanup(() => {
      const observer = new ResizeObserver(([entry]) => {
        this.observerEntry = entry;
      });
      observer.observe(this.refs.resize.value!);
      return () => {
        observer.disconnect();
      };
    });
  }

  render() {
    return html`
      <div class="menu" part="bible-menu-navigation-menu">
        <div
          ${ref(this.refs.content)}
          class="menu__content"
          part="bible-menu-navigation-menu-content"
          style=${styleMap(this.menuProperties)}
        >
          <div
            ${ref(this.refs.resize)}
            class="menu__resize"
            part="bible-menu-navigation-menu-resize"
          >
            <slot></slot>
          </div>
        </div>
      </div>
    `;
  }

  withCleanup(fn: () => () => void) {
    this.disconnectedCallbacks.push(fn());
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bible-navigation-menu": NavigationMenuElement;
  }
}

function getMenuProperties({
  width,
  height,
  offsetTop,
  triggerLeft,
  triggerWidth,
}: {
  width: number;
  height: number;
  offsetTop: number;
  triggerLeft: number;
  triggerWidth: number;
}) {
  // Notch height
  const nh = 10;
  // Notch width
  const nw = 30;
  // Height
  const h = height + nh;
  // Width
  const w = width;
  // Corner radius
  const r = 12;
  // Notch percentage (to align the notch and the trigger)
  const np = (triggerLeft + triggerWidth / 2) / w;
  const d = [
    // Top left
    `M${r},${nh}`,
    // Notch (bottom left)
    `h${w * np - nw / 2 - r}`,
    // Notch (top)
    `l${nw / 2},-${nh}`,
    // Notch (bottom right)
    `l${nw / 2},${nh}`,
    // Top right
    `h${w * (1 - np) - nw / 2 - r}`,
    // Top right corner
    `a${r},${r} 0 0 1 ${r},${r}`,
    // Right
    `v${h - r * 2 - nh * 2}`,
    // Bottom right corner
    `a${r},${r} 0 0 1 -${r},${r}`,
    // Bottom
    `h-${w - r * 2}`,
    // Bottom left corner
    `a${r},${r} 0 0 1 -${r},-${r}`,
    // Left
    `v-${h - r * 2 - nh * 2}`,
    // Top left corner
    `a${r},${r} 0 0 1 ${r},-${r}`,
    `z`,
  ];
  return {
    "--clip-path": `path('${d.join(" ")}')`,
    "--notch-height": `${nh}px`,
    "--offset-top": `${offsetTop}px`,
  };
}
